import { useMemo } from 'react';
import { format } from 'date-fns';
import { X } from 'lucide-react';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { DraggableSlot } from '@/components/calendar/DraggableSlot';
import { getUserColor, formatTime12Hour, computeRemainingAvailability } from '@/utils/calendarHelpers';
import type { DayData } from './types';

interface DayCellProps {
  day: DayData;
  onClick: () => void;
  currentUserId?: string;
  isClickable: boolean;
  isParentOrAdmin?: boolean;
  onRemoveWindow?: (dateString: string, windowToRemove: { start: string; end: string }, allWindows: { start_time: string; end_time: string }[]) => void;
  onDeleteSlot?: (slotId: string) => void;
}

export function DayCell({ day, onClick, currentUserId, isClickable, isParentOrAdmin, onRemoveWindow, onDeleteSlot }: DayCellProps) {
  const { date, dateString, isCurrentMonth, isToday: isTodayDate, availabilityWindows, claimedSlots } = day;
  const isMobile = useIsMobile();

  // Check if this day is in the past
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayDate = new Date(date);
  dayDate.setHours(0, 0, 0, 0);
  const isPast = dayDate < today;

  // Compute remaining availability (only for future/today)
  const remainingWindows = useMemo(() => {
    if (isPast) return [];
    return computeRemainingAvailability(availabilityWindows, claimedSlots);
  }, [availabilityWindows, claimedSlots, isPast]);

  return (
    <button
      data-testid={`clickable-day-cell-${dateString.replace(/-/g, '')}`}
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      className={`
        w-full h-full min-h-[70px] sm:min-h-[100px] p-1 sm:p-1.5 border-b border-r border-slate-700 text-left transition-colors relative
        ${isCurrentMonth ? 'bg-slate-900' : 'bg-slate-950'}
        ${isClickable && !isPast ? 'hover:bg-slate-800 cursor-pointer' : 'cursor-default'}
        ${isTodayDate ? 'ring-2 ring-inset ring-blue-500' : ''}
      `}
    >
      {/* Day number - upper left */}
      <div className="absolute top-1 left-1.5">
        <span className={`text-sm font-medium ${isCurrentMonth ? 'text-white' : 'text-gray-600'}`}>
          {format(date, 'd')}
        </span>
      </div>

      {/* Today badge - upper right */}
      {isTodayDate && (
        <div className="absolute top-1 right-1">
          <span className="text-[9px] bg-blue-500 text-white px-1 py-0.5 rounded">Today</span>
        </div>
      )}

      {/* Content area - below day number */}
      <div className="mt-5 space-y-0.5">
        {/* Remaining availability indicators */}
        {remainingWindows.slice(0, 2).map((window: any, idx: number) => (
          <DraggableSlot
            key={`avail-${dateString}-${idx}`}
            id={`avail-${dateString}-${window.start}-${window.end}`}
            data={{
              type: 'availability_window',
              date: dateString,
              start_time: window.start,
              end_time: window.end,
            }}
            disabled={!isParentOrAdmin || isMobile}
          >
            <div
              data-testid={`draggable-availability-${dateString.replace(/-/g, '')}-${window.start.replace(':', '')}-${window.end.replace(':', '')}`}
              className={`text-[9px] bg-emerald-500/20 text-emerald-400 px-1 py-0.5 rounded flex items-center gap-0.5 ${isParentOrAdmin && !isMobile ? 'cursor-grab active:cursor-grabbing' : ''}`}
            >
              <span className="truncate flex-1 min-w-0">{formatTime12Hour(window.start)}-{formatTime12Hour(window.end)}</span>
              {isParentOrAdmin && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveWindow?.(dateString, window, availabilityWindows);
                  }}
                  data-testid={`clickable-remove-window-${dateString.replace(/-/g, '')}-${window.start.replace(':', '')}-${window.end.replace(':', '')}`}
                  className="text-red-400 hover:text-red-300 rounded flex-shrink-0"
                  title="Remove this time slot"
                >
                  <X size={10} />
                </button>
              )}
            </div>
          </DraggableSlot>
        ))}
        {remainingWindows.length > 2 && (
          <div className="text-[9px] text-emerald-400">+{remainingWindows.length - 2} more</div>
        )}

        {/* All claimed slots - show time, emoji, and name */}
        {claimedSlots.filter((s: any) => s.status !== 'cancelled').slice(0, 2).map((slot: any) => {
          // Per-slot past check: for past days all slots dimmed; for today only slots ended before now
          const now = new Date();
          const slotEnd = new Date(`${slot.date}T${slot.end_time}:00`);
          const isSlotPast = isPast || (isTodayDate && slotEnd <= now);

          return (
            <DraggableSlot
              key={slot.id}
              id={`slot-${slot.id}`}
              data={{
                id: slot.id,
                caretaker_id: slot.caretaker_id,
                date: slot.date,
                start_time: slot.start_time,
                end_time: slot.end_time,
                caretaker: slot.caretaker,
              }}
              disabled={isSlotPast || !!slot.is_default || !isParentOrAdmin || isMobile}
            >
              <div
                data-testid={`draggable-shift-${dateString.replace(/-/g, '')}-${slot.start_time.replace(':', '')}-${slot.end_time.replace(':', '')}`}
                className={`text-[9px] px-1 py-0.5 rounded overflow-hidden ${isSlotPast ? 'opacity-50' : ''} ${!isSlotPast && !slot.is_default && isParentOrAdmin && !isMobile ? 'cursor-grab active:cursor-grabbing' : ''}`}
                style={{
                  backgroundColor: `${getUserColor(slot.caretaker)}25`,
                  borderLeft: `2px solid ${getUserColor(slot.caretaker)}`,
                  borderStyle: slot.is_default ? 'dashed' : 'solid',
                }}
              >
                <div className="text-gray-400 text-[8px]">
                  {formatTime12Hour(slot.start_time)}-{formatTime12Hour(slot.end_time)}
                </div>
                <div className="flex items-center gap-0.5">
                  {slot.caretaker?.emoji && <span className="flex-shrink-0">{slot.caretaker.emoji}</span>}
                  <span
                    className="truncate flex-1 min-w-0"
                    style={{ color: getUserColor(slot.caretaker, '#9ca3af') }}
                  >
                    {slot.caretaker?.first_name || 'Unknown'}
                  </span>
                  {!isSlotPast && !slot.is_default && onDeleteSlot && (isParentOrAdmin || slot.caretaker_id === currentUserId) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteSlot(slot.id);
                      }}
                      data-testid={`clickable-delete-slot-cell-${slot.id}`}
                      className="text-red-400 hover:text-red-300 rounded flex-shrink-0"
                      title={isParentOrAdmin ? 'Delete this slot' : 'Unclaim this slot'}
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>
              </div>
            </DraggableSlot>
          );
        })}

        {claimedSlots.filter((s: any) => s.status !== 'cancelled').length > 2 && (
          <div className="text-[9px] text-gray-500">+{claimedSlots.filter((s: any) => s.status !== 'cancelled').length - 2} more</div>
        )}
      </div>
    </button>
  );
}
